/**
 * Tree generators, one per vanilla species. Shapes follow the vanilla trunk
 * and foliage placers: blob canopies for oak / birch / jungle, layered cones
 * for spruce and pine, 2x2 trunks for dark oak and the mega trees, a bent
 * trunk with flat canopies for acacia, forked cherry, prop-rooted mangrove.
 * Heights are drawn per tree from the vanilla ranges.
 *
 * Every tree is a pure function of (seed, x, z): the RNG is seeded from the
 * root column, so the part of a tree inside a neighbouring chunk matches.
 */

import {
  ACACIA_LEAVES_ID, ACACIA_LOG_ID, AIR, BIRCH_LEAVES_ID, BIRCH_LOG_ID, BROWN_MUSHROOM_BLOCK_ID,
  CACTUS_ID, CHERRY_LEAVES_ID, CHERRY_LOG_ID, DARK_OAK_LEAVES_ID, DARK_OAK_LOG_ID, DIRT_ID, GRASS_ID,
  GRAVEL_ID, IS_OPAQUE, JUNGLE_LEAVES_ID, JUNGLE_LOG_ID, LEAVES_ID, LOG_ID, MANGROVE_LEAVES_ID,
  MANGROVE_LOG_ID, MUSHROOM_STEM_ID, PALE_OAK_LEAVES_ID, PALE_OAK_LOG_ID, PODZOL_ID, POWDER_SNOW_ID,
  RED_MUSHROOM_BLOCK_ID, RENDER_SHAPE, RenderShape, SNOW_BLOCK_ID, SNOW_ID, SPRUCE_LEAVES_ID,
  SPRUCE_LOG_ID, STONE_ID,
} from '../blocks';
import { Tree } from './biomes';
import { type GenCtx, getBlock, setBlock } from './ctx';

type Rng = () => number;

let ctx: GenCtx;
const int = (rnd: Rng, n: number): number => (rnd() * n) | 0;

function log(x: number, y: number, z: number, id: number): void {
  const c = getBlock(ctx, x, y, z);
  if (c === AIR || (c > 0 && IS_OPAQUE[c] === 0)) setBlock(ctx, x, y, z, id);
}

function leaf(x: number, y: number, z: number, id: number): void {
  const c = getBlock(ctx, x, y, z);
  if (c === AIR || (c > 0 && RENDER_SHAPE[c] !== RenderShape.Cube)) setBlock(ctx, x, y, z, id);
}

/** Vanilla puts dirt under every trunk. */
function soil(x: number, y: number, z: number): void {
  const c = getBlock(ctx, x, y, z);
  if (
    c === AIR || c === GRASS_ID || c === SNOW_ID || c === STONE_ID || c === GRAVEL_ID ||
    c === SNOW_BLOCK_ID || c === POWDER_SNOW_ID || c === PODZOL_ID
  ) setBlock(ctx, x, y, z, DIRT_ID);
}

function trunk(x: number, g: number, z: number, h: number, id: number): void {
  soil(x, g, z);
  for (let y = 1; y <= h; y++) log(x, g + y, z, id);
}

function trunk2(x: number, g: number, z: number, h: number, id: number): void {
  for (let dz = 0; dz <= 1; dz++) {
    for (let dx = 0; dx <= 1; dx++) {
      soil(x + dx, g, z + dz);
      // the ground under a 2x2 trunk is rarely level: fill down to it
      for (let y = 0; y >= -3 && getBlock(ctx, x + dx, g + y, z + dz) === AIR; y--) setBlock(ctx, x + dx, g + y, z + dz, id);
      for (let y = 1; y <= h; y++) log(x + dx, g + y, z + dz, id);
    }
  }
}

/** Square layer with the corners knocked off (always, or half the time). */
function square(x: number, y: number, z: number, r: number, id: number, rnd: Rng, corners: 'never' | 'random'): void {
  for (let dz = -r; dz <= r; dz++) {
    for (let dx = -r; dx <= r; dx++) {
      if (r > 0 && Math.abs(dx) === r && Math.abs(dz) === r && (corners === 'never' || rnd() < 0.5)) continue;
      leaf(x + dx, y, z + dz, id);
    }
  }
}

/** Disc around a (possibly fractional) centre. */
function disc(cx: number, y: number, cz: number, r: number, id: number): void {
  const r2 = r * r;
  for (let z = Math.floor(cz - r); z <= Math.ceil(cz + r); z++) {
    for (let x = Math.floor(cx - r); x <= Math.ceil(cx + r); x++) {
      const dx = x - cx, dz = z - cz;
      if (dx * dx + dz * dz <= r2) leaf(x, y, z, id);
    }
  }
}

/** Oak-style blob: two wide layers under two narrow ones, centred on the trunk top. */
function blob(x: number, top: number, z: number, id: number, rnd: Rng, wide = 0): void {
  for (let dy = -2; dy <= 1; dy++) {
    square(x, top + dy, z, (dy >= 0 ? 1 : 2) + wide, id, rnd, dy === 1 ? 'never' : 'random');
  }
}

/** Fancy-oak / jungle-branch leaf cluster: a squashed ball. */
function cluster(x: number, y: number, z: number, id: number): void {
  disc(x, y - 1, z, 1.6, id);
  disc(x, y, z, 2.5, id);
  disc(x, y + 1, z, 2.5, id);
  disc(x, y + 2, z, 1.6, id);
}

/** Straight-ish limb from (x,y,z) by (dx,dy,dz); returns nothing, places logs. */
function limb(x: number, y: number, z: number, dx: number, dy: number, dz: number, id: number): void {
  const n = Math.max(Math.abs(dx), Math.abs(dy), Math.abs(dz));
  for (let i = 1; i <= n; i++) {
    log(Math.round(x + (dx * i) / n), Math.round(y + (dy * i) / n), Math.round(z + (dz * i) / n), id);
  }
}

function oak(x: number, g: number, z: number, rnd: Rng, logId: number, leaves: number, base: number, extra: number): void {
  const h = base + int(rnd, extra + 1);
  blob(x, g + h, z, leaves, rnd);
  trunk(x, g, z, h, logId);
}

function fancyOak(x: number, g: number, z: number, rnd: Rng): void {
  const h = 6 + int(rnd, 6);
  trunk(x, g, z, h - 1, LOG_ID);
  cluster(x, g + h - 1, z, LEAVES_ID);
  const branches = 2 + int(rnd, 4);
  for (let i = 0; i < branches; i++) {
    const a = rnd() * Math.PI * 2;
    const len = 2 + rnd() * 2.5;
    const y0 = g + Math.floor(h * (0.4 + rnd() * 0.4));
    const dx = Math.round(Math.cos(a) * len), dz = Math.round(Math.sin(a) * len);
    const rise = 1 + int(rnd, 2);
    limb(x, y0, z, dx, rise, dz, LOG_ID);
    cluster(x + dx, y0 + rise, z + dz, LEAVES_ID);
  }
}

function swampOak(x: number, g: number, z: number, rnd: Rng): void {
  const h = 5 + int(rnd, 3);
  blob(x, g + h, z, LEAVES_ID, rnd, 1);
  trunk(x, g, z, h, LOG_ID);
}

/** Spruce: stacked discs that widen downward and reset, giving the tiered cone. */
function spruce(x: number, g: number, z: number, rnd: Rng): void {
  const h = 6 + int(rnd, 4);
  const bare = 1 + int(rnd, 2);
  const maxR = 2 + int(rnd, 2);
  let r = int(rnd, 2), lim = 1, min = 0;
  for (let y = g + h + 1; y > g + bare; y--) {
    square(x, y, z, y === g + h + 1 ? 0 : r, SPRUCE_LEAVES_ID, rnd, 'never');
    if (y === g + h + 1) continue;
    if (r >= lim) { r = min; min = 1; lim = Math.min(lim + 1, maxR); } else r++;
  }
  trunk(x, g, z, h, SPRUCE_LOG_ID);
}

/** Pine: tall bare trunk with a small crown at the very top. */
function pine(x: number, g: number, z: number, rnd: Rng): void {
  const h = 7 + int(rnd, 5);
  const top = g + h;
  square(x, top + 1, z, 0, SPRUCE_LEAVES_ID, rnd, 'never');
  square(x, top, z, 1, SPRUCE_LEAVES_ID, rnd, 'never');
  square(x, top - 1, z, 1, SPRUCE_LEAVES_ID, rnd, 'never');
  square(x, top - 2, z, 2, SPRUCE_LEAVES_ID, rnd, 'never');
  if (rnd() < 0.5) square(x, top - 3, z, 2, SPRUCE_LEAVES_ID, rnd, 'never');
  trunk(x, g, z, h, SPRUCE_LOG_ID);
}

/** Giant 2x2 spruce (deep crown) or pine (crown only near the top), with a podzol skirt. */
function megaConifer(x: number, g: number, z: number, rnd: Rng, pineCrown: boolean): void {
  const h = 14 + int(rnd, 3) + int(rnd, 12);
  const crown = pineCrown ? 3 + int(rnd, 5) : 13 + int(rnd, 5);
  const top = g + h;
  let prev = 0;
  for (let y = top; y >= top - crown && y > g + 2; y--) {
    const d = top - y;
    const r = Math.floor((d / crown) * 3.5);
    disc(x + 0.5, y, z + 0.5, r + (r === prev && (y & 1) === 0 ? 1.6 : 0.8), SPRUCE_LEAVES_ID);
    prev = r;
  }
  disc(x + 0.5, top + 1, z + 0.5, 0.8, SPRUCE_LEAVES_ID);
  trunk2(x, g, z, h, SPRUCE_LOG_ID);
  for (let dz = -3; dz <= 4; dz++) {
    for (let dx = -3; dx <= 4; dx++) {
      if ((dx - 0.5) * (dx - 0.5) + (dz - 0.5) * (dz - 0.5) > 11 + rnd() * 4) continue;
      for (let dy = 2; dy >= -2; dy--) {
        if (getBlock(ctx, x + dx, g + dy, z + dz) === GRASS_ID) { setBlock(ctx, x + dx, g + dy, z + dz, PODZOL_ID); break; }
      }
    }
  }
}

function megaJungle(x: number, g: number, z: number, rnd: Rng): void {
  const h = 12 + int(rnd, 3) + int(rnd, 15);
  const top = g + h;
  for (let dy = -2; dy <= 0; dy++) disc(x + 0.5, top + dy, z + 0.5, 2.6 - dy, JUNGLE_LEAVES_ID);
  // side branches up the trunk, each ending in a small canopy
  for (let y = top - 2 - int(rnd, 4); y > g + h / 2; y -= 2 + int(rnd, 4)) {
    const a = rnd() * Math.PI * 2;
    const dx = Math.round(Math.cos(a) * 4), dz = Math.round(Math.sin(a) * 4);
    limb(x, y, z, dx, 2, dz, JUNGLE_LOG_ID);
    disc(x + dx, y + 2, z + dz, 2.5, JUNGLE_LEAVES_ID);
    disc(x + dx, y + 3, z + dz, 1.5, JUNGLE_LEAVES_ID);
  }
  trunk2(x, g, z, h, JUNGLE_LOG_ID);
}

function jungleBush(x: number, g: number, z: number, rnd: Rng): void {
  soil(x, g, z);
  square(x, g + 1, z, 2, LEAVES_ID, rnd, 'random');
  square(x, g + 2, z, 1, LEAVES_ID, rnd, 'random');
  setBlock(ctx, x, g + 1, z, JUNGLE_LOG_ID);
}

function acaciaCanopy(x: number, y: number, z: number, rnd: Rng, r: number): void {
  square(x, y, z, r, ACACIA_LEAVES_ID, rnd, 'never');
  square(x, y + 1, z, r - 1, ACACIA_LEAVES_ID, rnd, 'never');
}

/** Acacia: the trunk leans away near the top; often a second, lower limb. */
function acacia(x: number, g: number, z: number, rnd: Rng): void {
  const h = 5 + int(rnd, 3) + int(rnd, 3);
  const a = int(rnd, 4);
  const dirX = [1, -1, 0, 0][a], dirZ = [0, 0, 1, -1][a];
  const bendAt = h - 1 - int(rnd, 4);
  let lean = 1 + int(rnd, 3);
  soil(x, g, z);
  let tx = x, tz = z, ty = g;
  for (let i = 1; i <= h; i++) {
    ty = g + i;
    if (i >= bendAt && lean > 0) { tx += dirX; tz += dirZ; lean--; }
    log(tx, ty, tz, ACACIA_LOG_ID);
  }
  acaciaCanopy(tx, ty, tz, rnd, 3);
  // secondary limb the other way
  if (rnd() < 0.7) {
    const b = (a + 1 + int(rnd, 3)) % 4;
    const bx = [1, -1, 0, 0][b], bz = [0, 0, 1, -1][b];
    const start = Math.max(2, bendAt - 1 - int(rnd, 2));
    const len = 1 + int(rnd, 3);
    let sx = x, sz = z, sy = g + start;
    for (let i = 0; i < len; i++) { sx += bx; sz += bz; sy++; log(sx, sy, sz, ACACIA_LOG_ID); }
    acaciaCanopy(sx, sy, sz, rnd, 2);
  }
}

/** Dark oak / pale oak: thick 2x2 trunk under a broad, flat-topped roof of leaves. */
function darkOak(x: number, g: number, z: number, rnd: Rng, logId: number, leaves: number): void {
  const h = 6 + int(rnd, 3) + int(rnd, 2);
  const top = g + h;
  const cx = x + 0.5, cz = z + 0.5;
  disc(cx, top - 1, cz, 3.7, leaves);
  disc(cx, top, cz, 3.2, leaves);
  disc(cx, top + 1, cz, 2.2, leaves);
  // stubby boughs poking out under the canopy
  for (let i = 0; i < 4; i++) {
    if (rnd() < 0.45) continue;
    const dx = i < 2 ? (i === 0 ? -1 : 2) : int(rnd, 2);
    const dz = i < 2 ? int(rnd, 2) : (i === 2 ? -1 : 2);
    const len = 1 + int(rnd, 3);
    for (let k = 0; k < len; k++) log(x + dx, top - 2 - k, z + dz, logId);
  }
  trunk2(x, g, z, h, logId);
}

/** Cherry: short trunk forking into one or two limbs, each under a wide drooping crown. */
function cherry(x: number, g: number, z: number, rnd: Rng): void {
  const h = 5 + int(rnd, 3);
  const crown = (cx: number, cy: number, cz: number): void => {
    disc(cx, cy + 2, cz, 1.6, CHERRY_LEAVES_ID);
    disc(cx, cy + 1, cz, 2.9, CHERRY_LEAVES_ID);
    disc(cx, cy, cz, 3.6, CHERRY_LEAVES_ID);
    disc(cx, cy - 1, cz, 3.3, CHERRY_LEAVES_ID);
    // drooping fringe
    for (let dz = -3; dz <= 3; dz++) {
      for (let dx = -3; dx <= 3; dx++) {
        const d = dx * dx + dz * dz;
        if (d >= 6 && d <= 11 && rnd() < 0.35) {
          leaf(cx + dx, cy - 2, cz + dz, CHERRY_LEAVES_ID);
          if (rnd() < 0.3) leaf(cx + dx, cy - 3, cz + dz, CHERRY_LEAVES_ID);
        }
      }
    }
  };
  const forks = 1 + int(rnd, 2);
  const a0 = int(rnd, 4);
  for (let f = 0; f < forks; f++) {
    const a = (a0 + f * 2) % 4;
    const dx = [1, -1, 0, 0][a], dz = [0, 0, 1, -1][a];
    const out = 2 + int(rnd, 2);
    const y0 = g + h - 2 - int(rnd, 2);
    for (let i = 1; i <= out; i++) log(x + dx * i, y0, z + dz * i, CHERRY_LOG_ID);
    const rise = 2 + int(rnd, 2);
    for (let i = 1; i <= rise; i++) log(x + dx * out, y0 + i, z + dz * out, CHERRY_LOG_ID);
    crown(x + dx * out, y0 + rise, z + dz * out);
  }
  if (forks === 1 || rnd() < 0.5) crown(x, g + h, z);
  trunk(x, g, z, h, CHERRY_LOG_ID);
}

/** Mangrove: trunk lifted on arched prop roots, standing in or beside water. */
function mangrove(x: number, g: number, z: number, rnd: Rng): void {
  const lift = 2 + int(rnd, 3);
  const h = lift + 4 + int(rnd, 4);
  const top = g + h;
  disc(x, top - 1, z, 3.3, MANGROVE_LEAVES_ID);
  disc(x, top, z, 2.6, MANGROVE_LEAVES_ID);
  disc(x, top + 1, z, 1.6, MANGROVE_LEAVES_ID);
  for (let y = lift; y <= h; y++) log(x, g + y, z, MANGROVE_LOG_ID);
  for (let i = 0; i < 4; i++) {
    if (rnd() < 0.2) continue;
    const dx = [1, -1, 0, 0][i], dz = [0, 0, 1, -1][i];
    const reach = 1 + int(rnd, 2);
    let rx = x, rz = z;
    for (let y = g + lift; y > g - 3; y--) {
      if (y < g + lift && Math.abs(rx - x) + Math.abs(rz - z) < reach) { rx += dx; rz += dz; }
      const c = getBlock(ctx, rx, y, rz);
      if (c > 0 && IS_OPAQUE[c] === 1) break;
      setBlock(ctx, rx, y, rz, MANGROVE_LOG_ID);
    }
  }
}

function hugeMushroom(x: number, g: number, z: number, rnd: Rng, red: boolean): void {
  const h = 4 + int(rnd, 3) + (rnd() < 0.08 ? 4 : 0);
  const top = g + h;
  if (red) {
    square(x, top, z, 1, RED_MUSHROOM_BLOCK_ID, rnd, 'never');
    setBlock(ctx, x - 1, top, z - 1, RED_MUSHROOM_BLOCK_ID); setBlock(ctx, x + 1, top, z - 1, RED_MUSHROOM_BLOCK_ID);
    setBlock(ctx, x - 1, top, z + 1, RED_MUSHROOM_BLOCK_ID); setBlock(ctx, x + 1, top, z + 1, RED_MUSHROOM_BLOCK_ID);
    for (let dy = 1; dy <= 3; dy++) {
      for (let dz = -2; dz <= 2; dz++) {
        for (let dx = -2; dx <= 2; dx++) {
          const edge = Math.max(Math.abs(dx), Math.abs(dz)) === 2;
          const corner = Math.abs(dx) === 2 && Math.abs(dz) === 2;
          if (edge && !corner) leaf(x + dx, top - dy, z + dz, RED_MUSHROOM_BLOCK_ID);
        }
      }
    }
  } else {
    square(x, top, z, 3, BROWN_MUSHROOM_BLOCK_ID, rnd, 'never');
  }
  for (let y = 1; y < h; y++) log(x, g + y, z, MUSHROOM_STEM_ID);
}

function cactus(x: number, g: number, z: number, rnd: Rng): void {
  const h = 1 + int(rnd, 3);
  for (let y = 1; y <= h; y++) {
    if (getBlock(ctx, x, g + y, z) === AIR) setBlock(ctx, x, g + y, z, CACTUS_ID);
  }
}

/** Grow a tree of `kind` rooted on ground block (x, g, z). */
export function growTree(c: GenCtx, kind: Tree, x: number, g: number, z: number, rnd: Rng): void {
  ctx = c;
  switch (kind) {
    case Tree.Oak: oak(x, g, z, rnd, LOG_ID, LEAVES_ID, 4, 2); break;
    case Tree.FancyOak: fancyOak(x, g, z, rnd); break;
    case Tree.SwampOak: swampOak(x, g, z, rnd); break;
    case Tree.Birch: oak(x, g, z, rnd, BIRCH_LOG_ID, BIRCH_LEAVES_ID, 5, 2); break;
    case Tree.TallBirch: oak(x, g, z, rnd, BIRCH_LOG_ID, BIRCH_LEAVES_ID, 7 + int(rnd, 5), 2); break;
    case Tree.Spruce: spruce(x, g, z, rnd); break;
    case Tree.Pine: pine(x, g, z, rnd); break;
    case Tree.MegaSpruce: megaConifer(x, g, z, rnd, false); break;
    case Tree.MegaPine: megaConifer(x, g, z, rnd, true); break;
    case Tree.Jungle: oak(x, g, z, rnd, JUNGLE_LOG_ID, JUNGLE_LEAVES_ID, 4, 8); break;
    case Tree.MegaJungle: megaJungle(x, g, z, rnd); break;
    case Tree.JungleBush: jungleBush(x, g, z, rnd); break;
    case Tree.Acacia: acacia(x, g, z, rnd); break;
    case Tree.DarkOak: darkOak(x, g, z, rnd, DARK_OAK_LOG_ID, DARK_OAK_LEAVES_ID); break;
    case Tree.PaleOak: darkOak(x, g, z, rnd, PALE_OAK_LOG_ID, PALE_OAK_LEAVES_ID); break;
    case Tree.Cherry: cherry(x, g, z, rnd); break;
    case Tree.Mangrove: mangrove(x, g, z, rnd); break;
    case Tree.HugeRedMushroom: hugeMushroom(x, g, z, rnd, true); break;
    case Tree.HugeBrownMushroom: hugeMushroom(x, g, z, rnd, false); break;
    case Tree.Cactus: cactus(x, g, z, rnd); break;
  }
}
